import { Disclosure } from "@design";
import { Telescope } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Text } from "@/components/Text";
import { formatNumber } from "@/i18n/format";
import { ResearchCounts, ResearchTopics } from "./ResearchTopics";
import { partialNotice, type ResearchErrorCode, type ResearchSummaryView } from "./researchModel";

/** "6 min", or "45 sec" for a run under a minute. */
function runTime(durationMs: number) {
  const seconds = Math.max(0, Math.round(durationMs / 1000));
  return seconds < 60
    ? formatNumber(seconds, { style: "unit", unit: "second", unitDisplay: "short" })
    : formatNumber(Math.round(seconds / 60), { style: "unit", unit: "minute", unitDisplay: "short" });
}

/**
 * A saved research report's run, in one line ("Researched 42 sources in 6 min") that opens to its topics and counts,
 * and a notice when the report was cut short.
 */
export function ResearchSummary({ research }: Readonly<{ research: ResearchSummaryView }>) {
  const { t } = useTranslation("chat", { keyPrefix: "research" });
  const extra = [
    ...(research.rounds > 0 ? [t("summary.rounds", { count: research.rounds })] : []),
    t("summary.depth", { depth: t(`proposal.depthOption.${research.depth}`) }),
  ];
  return (
    <div className="space-y-2">
      <Disclosure
        icon={<Telescope size={12} className="shrink-0" />}
        label={t("summary.label", { count: research.counts.sources, time: runTime(research.durationMs) })}
      >
        <div className="space-y-3 p-3">
          <ResearchTopics topics={research.topics} />
          <ResearchCounts counts={research.counts} extra={extra} />
        </div>
      </Disclosure>
      {research.partial && (
        <div className="rounded-lg bg-warning-subtle p-3">
          <Text variant="caption" tone="warning">
            {t(`summary.partial.${partialNotice(research.partialReason)}`)}
          </Text>
        </div>
      )}
    </div>
  );
}

/** A saved reply for a research run that failed, explained from its code (the saved text is the backend's). */
export function ResearchFailure({ code }: Readonly<{ code: ResearchErrorCode }>) {
  const { t } = useTranslation("chat");
  return <Text tone="error">{t(`reply.errorCodes.${code}`)}</Text>;
}
